// Phase COTTON-RM-002 Part C — Mixing Readiness Report
//
// Tabs: Ready · Blocked · Reserved · Issued · Consumed · Common Blockers.

import { useState } from 'react'
import {
  Badge, Button, Card, Empty, Select, Skeleton, Space, Table, Tabs, Tag,
  Typography,
} from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import apiClient from '@/shared/api/client'
import {
  cottonRmApiPartC, type BlockedBale, type ReadyBale,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

interface MovedBale {
  bale_id: string
  bale_no: string
  weight_kg: number
  effective_cost_per_kg: number | null
  godown: string | null
}

function csvDownload<T extends Record<string, unknown>>(rows: T[], name: string) {
  if (rows.length === 0) return
  const cols = Object.keys(rows[0])
  const header = cols.join(',')
  const body = rows.map(r =>
    cols.map(c => JSON.stringify(
      (r as Record<string, unknown>)[c] ?? '',
    )).join(',')).join('\n')
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `${name}.csv`
  a.click()
  URL.revokeObjectURL(a.href)
}


function ReadyTable({ rows }: { rows: ReadyBale[] }) {
  if (rows.length === 0) return <Empty description="No ready bales." />
  return (
    <Table rowKey="bale_id" size="small"
      dataSource={rows}
      columns={[
        { title: 'Bale', dataIndex: 'bale_no',
          render: (v: string, r: ReadyBale) =>
            <Link to={`/inventory/bales/traceability/${r.bale_id}`}>{v}</Link> },
        { title: 'Godown', dataIndex: 'godown' },
        { title: 'Weight (kg)', dataIndex: 'weight_kg',
          render: (n: number) => n?.toFixed(1) ?? '' },
        { title: '₹/kg', dataIndex: 'effective_cost_per_kg',
          render: (n: number | null) => n !== null ? `₹${n.toFixed(2)}` : '' },
      ] as never}
      pagination={{ pageSize: 50 }}
      title={() => (
        <Space>
          <Text>{rows.length} bales</Text>
          <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => csvDownload(rows as never, 'ready-bales')}>
            Export CSV
          </Button>
        </Space>
      )} />
  )
}


function BlockedTable({ rows }: { rows: BlockedBale[] }) {
  if (rows.length === 0) return <Empty description="No blocked bales." />
  return (
    <Table rowKey="bale_id" size="small"
      dataSource={rows}
      columns={[
        { title: 'Bale', dataIndex: 'bale_no',
          render: (v: string, r: BlockedBale) =>
            <Link to={`/inventory/bales/traceability/${r.bale_id}`}>{v}</Link> },
        { title: 'Godown', dataIndex: 'godown' },
        { title: 'Weight (kg)', dataIndex: 'weight_kg',
          render: (n: number | null) => n?.toFixed(1) ?? '' },
        { title: '₹/kg', dataIndex: 'effective_cost_per_kg',
          render: (n: number | null) => n !== null ? `₹${n.toFixed(2)}` : '' },
        { title: 'Blocking reasons', dataIndex: 'blocking_reasons',
          render: (rs: string[]) => (
            <Space size={4} wrap>
              {rs.map(r => <Tag color="warning" key={r}>{r}</Tag>)}
            </Space>
          ) },
      ] as never}
      pagination={{ pageSize: 50 }}
      title={() => (
        <Space>
          <Text>{rows.length} bales</Text>
          <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => csvDownload(
                    rows.map(r => ({ ...r, blocking_reasons: r.blocking_reasons.join('|') })) as never,
                    'blocked-bales')}>
            Export CSV
          </Button>
        </Space>
      )} />
  )
}


function MovedTable({ rows, kind }: { rows: MovedBale[]; kind: string }) {
  if (rows.length === 0) return <Empty description={`No ${kind} bales.`} />
  return (
    <Table rowKey="bale_id" size="small"
      dataSource={rows}
      columns={[
        { title: 'Bale', dataIndex: 'bale_no',
          render: (v: string, r: MovedBale) =>
            <Link to={`/inventory/bales/traceability/${r.bale_id}`}>{v}</Link> },
        { title: 'Godown', dataIndex: 'godown' },
        { title: 'Weight (kg)', dataIndex: 'weight_kg',
          render: (n: number) => n?.toFixed(1) ?? '' },
        { title: '₹/kg', dataIndex: 'effective_cost_per_kg',
          render: (n: number | null) => n !== null ? `₹${n.toFixed(2)}` : '' },
      ] as never}
      pagination={{ pageSize: 50 }}
      title={() => (
        <Space>
          <Text>{rows.length} bales</Text>
          <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => csvDownload(rows as never, `${kind}-bales`)}>
            Export CSV
          </Button>
        </Space>
      )} />
  )
}


export default function MixingReadinessReportPage() {
  const [plantId, setPlantId] = useState<string | undefined>()

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-mixing-rdy'],
    queryFn: async () =>
      (await apiClient.get<{ id: string; plant_name: string }[]>('/plants')).data,
  })

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['mixing-readiness-report', plantId],
    queryFn: () => cottonRmApiPartC.mixingReadiness({ plant_id: plantId }),
  })

  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Mixing Readiness Report</Title>
      <Text type="secondary">
        Bales ready to feed the mixing line, bales blocked by missing
        QC / cost / location, and a roll-up of the most common
        blockers across the inventory.
      </Text>

      <Space wrap style={{ marginTop: 12, marginBottom: 8 }}>
        <Select placeholder="All plants" allowClear style={{ width: 220 }}
                value={plantId} onChange={setPlantId}
                options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
        <Button icon={<ReloadOutlined spin={isFetching} />}
                onClick={() => refetch()}>Refresh</Button>
      </Space>

      <Card>
        {isLoading || !data ? <Skeleton active /> : (
          <Tabs items={[
            { key: 'ready',
              label: <Space size={4}>Ready <Badge count={data.ready_count}
                                                 style={{ backgroundColor: '#52c41a' }} /></Space>,
              children: <ReadyTable rows={data.ready} /> },
            { key: 'blocked',
              label: <Space size={4}>Blocked <Badge count={data.blocked_count}
                                                   style={{ backgroundColor: '#faad14' }} /></Space>,
              children: <BlockedTable rows={data.blocked} /> },
            { key: 'reserved',
              label: <Space size={4}>Reserved <Badge count={data.reserved_count} /></Space>,
              children: <MovedTable rows={data.reserved as MovedBale[]} kind="reserved" /> },
            { key: 'issued',
              label: <Space size={4}>Issued <Badge count={data.issued_count} /></Space>,
              children: <MovedTable rows={data.issued as MovedBale[]} kind="issued" /> },
            { key: 'consumed',
              label: <Space size={4}>Consumed <Badge count={data.consumed_count} /></Space>,
              children: <MovedTable rows={data.consumed as MovedBale[]} kind="consumed" /> },
            { key: 'common',
              label: 'Common Blockers',
              children: data.common_blockers.length === 0 ? <Empty /> : (
                <Table rowKey={(r: unknown) => (r as [string, number])[0]} size="small"
                  pagination={false}
                  dataSource={data.common_blockers as never[]}
                  columns={[
                    { title: 'Blocker',
                      render: (_: unknown, r: unknown) => (r as [string, number])[0] },
                    { title: 'Bales affected', align: 'right' as const,
                      render: (_: unknown, r: unknown) => (r as [string, number])[1] },
                  ]} />
              ) },
          ]} />
        )}
      </Card>
    </div>
  )
}
